import { useState, useMemo, useRef, useEffect } from "react";

import type { AutomationTarget } from "@/lib/types";

import { useSlackChannels } from "@/hooks/useSlackBroadcast";

interface TargetSelectorProps {
  value: AutomationTarget;
  onChange: (target: AutomationTarget) => void;
}

export function TargetSelector({ value, onChange }: TargetSelectorProps): JSX.Element {
  const { data, isLoading } = useSlackChannels();
  const [search, setSearch] = useState(() => (value.source === "slack" ? "" : ""));
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedChannelName = useMemo(() => {
    if (value.source !== "slack") return null;
    return data?.channels.find((ch) => ch.id === value.channel)?.name ?? null;
  }, [data?.channels, value]);

  const filteredChannels = useMemo(() => {
    if (!data?.channels) return [];
    if (!search) return data.channels;
    const lower = search.toLowerCase();
    return data.channels.filter((ch) => ch.name.toLowerCase().includes(lower));
  }, [data?.channels, search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredChannels]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen]);

  function handleSelect(channel: { id: string; name: string }) {
    onChange({ source: "slack", channel: channel.id });
    setSearch(channel.name);
    setIsOpen(false);
  }

  function handleWebOnly() {
    onChange({ source: "web" });
    setSearch("");
    setIsOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filteredChannels.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredChannels[highlightedIndex]) {
          handleSelect(filteredChannels[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleWebOnly}
          className={`rounded px-2.5 py-1 text-xs transition-all ${
            value.source === "web"
              ? "border border-cyber-cyan/40 bg-cyber-cyan/20 text-cyber-cyan"
              : "border border-cyber-border text-cyber-muted hover:text-cyber-text"
          }`}
        >
          Web only
        </button>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={`rounded px-2.5 py-1 text-xs transition-all ${
            value.source === "slack"
              ? "border border-cyber-cyan/40 bg-cyber-cyan/20 text-cyber-cyan"
              : "border border-cyber-border text-cyber-muted hover:text-cyber-text"
          }`}
        >
          {value.source === "slack" && selectedChannelName
            ? `#${selectedChannelName}`
            : "Slack channel"}
        </button>
      </div>

      {(value.source === "slack" || isOpen) && (
        <div className="relative">
          {isLoading ? (
            <div className="text-xs text-cyber-muted">
              <span className="animate-spin-glow mr-1 inline-block">◐</span>
              Loading channels...
            </div>
          ) : (
            <>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-cyber-muted">
                  #
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search channels..."
                  className="w-full rounded border border-cyber-border bg-cyber-bg py-1.5 pl-7 pr-3 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
                  autoComplete="off"
                />
              </div>

              {isOpen && filteredChannels.length > 0 && (
                <div
                  ref={listRef}
                  className="absolute left-0 right-0 top-full z-10 mt-1 max-h-40 overflow-y-auto rounded border border-cyber-border bg-cyber-bg shadow-lg"
                >
                  {filteredChannels.map((ch, index) => (
                    <div
                      role="option"
                      aria-selected={index === highlightedIndex}
                      key={ch.id}
                      onClick={() => handleSelect(ch)}
                      className={`cursor-pointer px-3 py-1.5 text-sm ${
                        index === highlightedIndex
                          ? "bg-cyber-cyan/20 text-cyber-cyan"
                          : "text-cyber-text hover:bg-cyber-cyan/10"
                      }`}
                    >
                      #{ch.name}
                    </div>
                  ))}
                </div>
              )}

              {isOpen && search && filteredChannels.length === 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 rounded border border-cyber-border bg-cyber-bg px-3 py-1.5 text-xs text-cyber-muted">
                  No channels found
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
